import {Typography, css} from '@mui/material';
import {DlMuiPopper} from '@wallaby-ui/core';
import * as React from 'react';

export const DL_TOOLTIP_ATTR = 'data-dl-tooltip';

// Maximally lightweight ATB cell. Renders the SAME visual as <ProgressBarFormatter centerText> wrapped
// in <Typography variant="value">, but with plain <div>s and a STATIC stylesheet (no per-cell emotion
// css-prop runtime / MUI Typography theme lookup), no per-cell Popper, and React.memo. The tooltip is
// delegated to AntibiogramTooltipBoundary's single shared popper via the data attribute.
const ATB_CELL_CLASS = {
  root: 'dl-atb-cell',
  wrapper: 'dl-atb-cell__wrapper',
  bar: 'dl-atb-cell__bar',
  span: 'dl-atb-cell__span',
};

// Inject the cell stylesheet ONCE. Using plain classes (instead of the emotion css-prop) removes the
// per-cell emotion runtime. The rules are
// byte-identical to the previous emotion styles, and nothing else targets these class names, so the
// cascade is unaffected. Mirrors: <Typography variant="value"> + ProgressBarFormatter(centerText).
const ATB_CELL_STYLE_ID = 'dl-atb-cell-styles';
if (typeof document !== 'undefined' && !document.getElementById(ATB_CELL_STYLE_ID)) {
  const styleEl = document.createElement('style');
  styleEl.id = ATB_CELL_STYLE_ID;
  styleEl.textContent = [
    `.${ATB_CELL_CLASS.root}{display:block;margin:0;font-family:SourceSansPro-Regular;font-size:14px;line-height:18px;}`,
    `.${ATB_CELL_CLASS.wrapper}{width:100%;height:26px;position:relative;}`,
    `.${ATB_CELL_CLASS.bar}{position:absolute;top:0;left:0;width:100%;height:100%;border-radius:4px;}`,
    `.${ATB_CELL_CLASS.span}{position:absolute;top:0;left:0;width:100%;height:100%;padding:4px 0;text-align:center;display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}`,
  ].join('\n');
  document.head.appendChild(styleEl);
}

type AtbCellProps = {
  progressPercent: number;
  value: React.ReactNode;
  background: string;
  tooltip: string;
};

export const AtbCell: React.FC<AtbCellProps> = React.memo(function AtbCell({progressPercent, value, background, tooltip}) {
  return (
    <div className={ATB_CELL_CLASS.root}>
      <div className={ATB_CELL_CLASS.wrapper} {...(tooltip ? {[DL_TOOLTIP_ATTR]: tooltip} : {})}>
        <div className={ATB_CELL_CLASS.bar} style={{background, width: `${progressPercent}%`}} />
        <span className={ATB_CELL_CLASS.span}>{value}</span>
      </div>
    </div>
  );
});
AtbCell.displayName = 'AtbCell';

// Single shared tooltip for the whole table. One DlMuiPopper (identical styling to the per-cell one),
// portaled to <body>, anchored to the HOVERED CELL and centered below it (placement='bottom'), driven by
// ONE delegated mouseover listener.
// The shared popper must NOT capture pointer events, else (overlapping the cell below) it would steal the
// hover from the cell underneath, causing a show/hide flicker.
const popperNoPointerEvents = css`
  pointer-events: none;
`;
// Gap (px) between the cell's bottom edge and the tooltip.
const TOOLTIP_CELL_GAP = 6;
const TOOLTIP_OFFSET_MODIFIER = [{name: 'offset', options: {offset: [0, TOOLTIP_CELL_GAP]}}];

type Tip = {el: HTMLElement; text: string} | null;

type BoundaryProps = React.HTMLAttributes<HTMLDivElement> & {children: React.ReactNode};

export const AntibiogramTooltipBoundary: React.FC<BoundaryProps> = (props) => {
  const {children, onMouseOver, onMouseLeave, ...rest} = props;
  const [tip, setTip] = React.useState<Tip>(null);

  const handleOver = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseOver?.(e);
      const cell = (e.target as HTMLElement | null)?.closest(`[${DL_TOOLTIP_ATTR}]`) as HTMLElement | null;
      if (!cell) {
        setTip((p) => (p ? null : p));
        return;
      }
      // Anchor to the cell's <td> so the tooltip is centered BELOW the cell (not at the cursor).
      const anchor = (cell.closest('td') as HTMLElement | null) ?? cell;
      // Update only when entering a different cell (cheap: one state update per cell, not per mousemove).
      setTip((p) => (p && p.el === anchor ? p : {el: anchor, text: cell.getAttribute(DL_TOOLTIP_ATTR) ?? ''}));
    },
    [onMouseOver]
  );

  const handleLeave = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      setTip((p) => (p ? null : p));
    },
    [onMouseLeave]
  );

  return (
    <div {...rest} onMouseOver={handleOver} onMouseLeave={handleLeave}>
      {children}
      <DlMuiPopper
        hasTooltip={!!tip}
        open={!!tip}
        anchorEl={tip?.el ?? null}
        placement='bottom'
        modifiers={TOOLTIP_OFFSET_MODIFIER}
        inputCss={popperNoPointerEvents}
      >
        <Typography variant={'popper'}>{tip?.text}</Typography>
      </DlMuiPopper>
    </div>
  );
};
AntibiogramTooltipBoundary.displayName = 'AntibiogramTooltipBoundary';
